import { Box } from "@chakra-ui/react";
import type { ReactElement } from "react";
import { FiFileText, FiSearch } from "react-icons/fi";
import { Home } from "./home/Home";
import { NotificationsPageV2 } from "./notifications/NotificationsPageV2";
import { ContractStatusDto } from "../../autogen/bff-api";
import { urls } from "../../urls";
import { EditContract } from "../contracts/edit/EditContract";
import { Contracts } from "../contracts/view-all/Contracts";
import { ContractPage } from "../contracts/view-single/ContractPage";
import { AccessManagementPage } from "../organizations/current/manage-users/AccessManagementPage";
import { EditOrganizationProfile } from "../organizations/current/edit/EditOrganizationProfile";
import { OrganizationsPage } from "../organizations/search/OrganizationsPage";
import { ViewOrganizationProfile } from "../organizations/view-single/ViewOrganizationProfile";
import { Projects } from "../projects/view-all/Projects";
import { ProjectPage } from "../projects/view-single/ProjectPage";
import { EditBasicSourcingEvent } from "../sourcing-events/buyer/edit/EditBasicSourcingEvent";
import { EditPublicSourcingEvent } from "../sourcing-events/buyer/public/EditPublicSourcingEvent";
import { BasicSourcingEventView } from "../sourcing-events/buyer/view-single/BasicSourcingEventView";
import { Behovsliste } from "./behov/Behovsliste";
import { EditNeed } from "./behov/EditNeed";
import { MineBehov } from "./behov/MineBehov";
import { NeedView } from "./behov/NeedView";
import { NewNeed } from "./behov/NewNeed";
import { PlaceholderPage } from "./PlaceholderPage";
import { EditProfilePage } from "./profile/EditProfilePage";
import { ProfilePage } from "./profile/ProfilePage";
import { SettingsPage } from "./settings/SettingsPage";
import { SourcingEventsList } from "./SourcingEventsList";
import { BidDetailView } from "./sourcing-event/bid-detail/BidDetailView";
import { ProtocolPage } from "./sourcing-event/protocol/ProtocolPage";
import { SourcingEventView } from "./sourcing-event/SourcingEventView";

export const v2Routes: { path: string; element: ReactElement }[] = [
  { path: urls.v2.home, element: <Home /> },
  { path: urls.v2.notifications, element: <NotificationsPageV2 /> },
  { path: urls.v2.needs.new, element: <NewNeed /> },
  { path: urls.v2.needs.edit.pathname, element: <EditNeed /> },
  { path: urls.v2.needs.view.pathname, element: <NeedView /> },
  { path: urls.v2.needs.mine, element: <MineBehov /> },
  { path: urls.v2.needs.list, element: <Behovsliste /> },
  { path: urls.v2.marketSurveys.list, element: <PlaceholderPage titleKey="Market surveys" descriptionKey="Explore the market before you buy." icon={FiSearch} /> },
  { path: urls.v2.sourcingEvents.viewAll, element: <SourcingEventsList /> },
  { path: urls.v2.sourcingEvents.view.pathname, element: <SourcingEventView /> },
  { path: urls.v2.sourcingEvents.bidDetail.pathname, element: <BidDetailView /> },
  { path: urls.v2.sourcingEvents.protocol.pathname, element: <ProtocolPage /> },
  {
    path: urls.v2.sourcingEvents.bseView.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <BasicSourcingEventView embedded />
      </Box>
    ),
  },
  {
    path: urls.publicEvents.edit.paths.info.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditPublicSourcingEvent />
      </Box>
    ),
  },
  {
    path: urls.v2.sourcingEvents.bseEdit.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditBasicSourcingEvent embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.contracts.viewAll,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <Contracts key="contracts-all" embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.contracts.active,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <Contracts key="contracts-active" embedded defaultStatuses={[ContractStatusDto.Active, ContractStatusDto.Awarded]} />
      </Box>
    ),
  },
  {
    path: urls.v2.contracts.view.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <ContractPage embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.contracts.edit.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <EditContract embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.projects.viewAll,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder">
        <Projects embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.projects.view.pathname,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="6">
        <ProjectPage embedded />
      </Box>
    ),
  },
  { path: urls.v2.profile, element: <ProfilePage /> },
  { path: urls.v2.profileEdit, element: <EditProfilePage /> },
  { path: urls.v2.settings, element: <SettingsPage /> },
  { path: urls.v2.templates, element: <PlaceholderPage titleKey="Templates" descriptionKey="Document templates." icon={FiFileText} /> },
  {
    path: urls.v2.users,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px="6" pb="6">
        <AccessManagementPage embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.organizations,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} py="2">
        <OrganizationsPage embedded />
      </Box>
    ),
  },
  {
    path: `${urls.v2.organizationProfile.pathname}/*`,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" overflow="hidden">
        <ViewOrganizationProfile embedded />
      </Box>
    ),
  },
  {
    path: urls.v2.organizationProfileEdit,
    element: (
      <Box bg="smBackground" rounded="lg" border="1px solid" borderColor="smBorder" px={{ base: "4", md: "6" }} pb="6">
        <EditOrganizationProfile embedded />
      </Box>
    ),
  },
];
